import React from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import useVatReportService from "./useVatReportService";
import {
  checkVatReportsAvailability,
  getCachedVatReportsAvailability,
  getVatReportsAvailabilityCacheKey,
} from "utils/helpers/vatReportsAvailabilityHelper";

interface CheckState {
  key?: string;
  available: boolean;
  failed: boolean;
  loading: boolean;
}

const UNASKED = { available: false, failed: false, loading: false };

/**
 * Whether the selected company has VAT reports to show the signed-in advisor. `failed` means the
 * question could not be answered; `retry` asks again.
 */
function useVatReportsAvailability(enabled: boolean = true) {
  const companyId = useSelector((store: AppState) => store.auth.selectedCompany?.id);
  const personId = useSelector((store: AppState) => store.auth.loggedInUser?.personId);
  const { getAvailability } = useVatReportService();

  const key = enabled && companyId !== undefined ? getVatReportsAvailabilityCacheKey(personId, companyId) : undefined;
  const cached = key !== undefined ? getCachedVatReportsAvailability(key) : undefined;

  const [state, setState] = React.useState<CheckState>(UNASKED);
  const [attempt, setAttempt] = React.useState(0);

  const retry = React.useCallback(() => {
    // Loading straight away, so the failed state does not linger for the render before the effect.
    setState((current) => ({ ...current, failed: false, loading: true }));
    setAttempt((current) => current + 1);
  }, []);

  React.useEffect(() => {
    if (key === undefined) {
      setState(UNASKED);
      return;
    }

    const resolved = getCachedVatReportsAvailability(key);
    if (resolved !== undefined) {
      setState({ key, available: resolved, failed: false, loading: false });
      return;
    }

    // Reset together with `loading`, so the previous company's answer never stands in for this one.
    let mounted = true;
    setState({ key, available: false, failed: false, loading: true });
    checkVatReportsAvailability(key, getAvailability).then(({ available, failed }) => {
      if (mounted) setState({ key, available, failed, loading: false });
    });

    return () => {
      mounted = false;
    };
    // `getAvailability` is fresh each render; the key and a retry are what decide a refetch.
  }, [key, attempt]);

  if (key === undefined) return { ...UNASKED, retry };
  if (cached !== undefined) return { available: cached, failed: false, loading: false, retry };
  // State still describing the previous key covers the render before the effect above has run.
  if (state.key !== key) return { available: false, failed: false, loading: true, retry };
  return { available: state.available, failed: state.failed, loading: state.loading, retry };
}

export default useVatReportsAvailability;
